import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getGizmosGame, getGizmosGameState, performGizmosAction } from '../api/gizmos';
import { useGizmosGameWebSocket } from '../hooks/useWebSocket';
import GizmosBoard from '../components/GizmosBoard/GizmosBoard';

export default function GizmosGamePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [gameMeta, setGameMeta] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [stateVersion, setStateVersion] = useState(0);
  const [pageError, setPageError] = useState('');
  const [actionError, setActionError] = useState('');

  const loadAll = useCallback(async () => {
    try {
      const [metaRes, stateRes] = await Promise.all([
        getGizmosGame(id),
        getGizmosGameState(id),
      ]);
      setGameMeta(metaRes.data);
      setGameState(stateRes.data.state);
      setStateVersion(stateRes.data.state_version);
      if (['waiting', 'ready'].includes(metaRes.data.status)) {
        navigate(`/gizmos/${id}/room`);
      }
    } catch {
      setPageError('加载游戏失败，请检查后端是否运行');
    }
  }, [id, navigate]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleWsMessage = useCallback((data) => {
    if (data.type === 'state.update') {
      if (data.state) {
        setGameState(data.state);
        setStateVersion(data.state_version);
      } else {
        loadAll();
      }
    }
    if (data.game_status) {
      setGameMeta(prev => prev ? { ...prev, status: data.game_status } : prev);
    }
  }, [loadAll]);

  const { closeCode } = useGizmosGameWebSocket(id, handleWsMessage, { onOpen: loadAll });

  useEffect(() => {
    if (closeCode === 4001) setPageError('实时连接认证失败，请重新登录');
    else if (closeCode === 4003) setPageError('你不在这局游戏中');
  }, [closeCode]);

  const handleAction = useCallback(async (type, payload) => {
    setActionError('');
    try {
      const { data } = await performGizmosAction(id, type, payload, stateVersion);
      setGameState(data.state);
      setStateVersion(data.state_version);
      if (data.game_status) setGameMeta(prev => ({ ...prev, status: data.game_status }));
      return data;
    } catch (err) {
      const msg = err.response?.data?.detail || '操作失败';
      setActionError(msg);
      if (err.response?.status === 409) loadAll();
      throw err;
    }
  }, [id, stateVersion, loadAll]);

  if (pageError) return <div className="loading-page">{pageError}</div>;
  if (!gameMeta || !gameState) return <div className="loading-page">加载游戏中...</div>;

  return (
    <GizmosBoard
      gameMeta={gameMeta}
      gameState={gameState}
      currentUserId={Number(user?.id)}
      onAction={handleAction}
      onLeave={() => navigate('/gizmos/lobby')}
      actionError={actionError}
    />
  );
}
